import { ArrowRight, Building2, CalendarDays, IndianRupee } from "lucide-react";
import { Container, Reveal, SectionHeader } from "./common";

const columns = [
    {
        stage: "New Lead", color: "#64748B",
        deals: [
            { company: "Skyline Constructions", value: "₹12.5L", owner: "RS", close: "Aug 12", prob: 20 },
            { company: "PeakFin Advisors", value: "₹3.2L", owner: "PN", close: "Aug 20", prob: 15 },
        ],
    },
    {
        stage: "Qualified", color: "#3B82F6",
        deals: [
            { company: "Orbit Staffing", value: "₹6.8L", owner: "AT", close: "Aug 08", prob: 35 },
            { company: "GreenLeaf Agro", value: "₹4.1L", owner: "RS", close: "Aug 15", prob: 40 },
        ],
    },
    {
        stage: "Discovery", color: "#8B5CF6",
        deals: [
            { company: "Northwind Realty", value: "₹9.4L", owner: "PN", close: "Aug 05", prob: 50 },
            { company: "TechVista Solutions", value: "₹7.6L", owner: "RS", close: "Aug 22", prob: 45 },
        ],
    },
    {
        stage: "Proposal", color: "#F59E0B",
        deals: [
            { company: "Meridian Estates", value: "₹18.2L", owner: "AT", close: "Jul 30", prob: 65 },
            { company: "FinEdge Capital", value: "₹5.5L", owner: "PN", close: "Aug 02", prob: 60 },
        ],
    },
    {
        stage: "Negotiation", color: "#F97316",
        deals: [
            { company: "UrbanSpace Group", value: "₹22.8L", owner: "RS", close: "Jul 28", prob: 80 },
        ],
    },
    {
        stage: "Won", color: "#16A34A",
        deals: [
            { company: "Apex Manufacturing", value: "₹14.6L", owner: "AT", close: "Closed", prob: 100 },
            { company: "BrightHire HR", value: "₹4.9L", owner: "PN", close: "Closed", prob: 100 },
        ],
    },
];

const Pipeline = () => (
    <section id="pipeline" data-testid="pipeline-kanban-section" className="bg-white py-20 lg:py-28">
        <Container>
            <SectionHeader
                eyebrow="Sales Pipeline"
                title="Turn opportunities into revenue."
                sub="A living pipeline your team actually updates — drag deals between stages, see weighted forecasts, and know exactly where every rupee sits."
            />
            <Reveal delay={0.15}>
                <div data-testid="pipeline-kanban-board-mockup" className="mt-14 overflow-hidden rounded-2xl border border-[#E5E7EB] bg-[#FBFDFC] shadow-xl shadow-slate-900/[0.06]">
                    <div className="flex items-center justify-between border-b border-[#F1F5F9] bg-white px-5 py-3.5">
                        <p className="font-display text-sm font-bold text-[#111827]">Q3 Sales Pipeline</p>
                        <div className="flex items-center gap-3">
                            <span className="hidden items-center gap-1.5 rounded-full bg-[#F0FDF4] px-3 py-1 text-[11px] font-bold text-[#15803D] sm:flex">
                                <IndianRupee className="h-3 w-3" /> ₹1.15Cr open
                            </span>
                            <span className="rounded-full bg-[#111827] px-3 py-1 text-[11px] font-bold text-white">11 deals</span>
                        </div>
                    </div>
                    <div className="flex gap-3 overflow-x-auto p-4 pb-5">
                        {columns.map((col) => (
                            <div key={col.stage} className="w-[220px] shrink-0">
                                <div className="mb-2.5 flex items-center justify-between px-1">
                                    <span className="flex items-center gap-1.5 text-[11px] font-bold text-[#334155]">
                                        <span className="h-2 w-2 rounded-full" style={{ background: col.color }} />
                                        {col.stage}
                                    </span>
                                    <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-bold text-[#94A3B8] ring-1 ring-[#E5E7EB]">
                                        {col.deals.length}
                                    </span>
                                </div>
                                <div className="grid gap-2.5">
                                    {col.deals.map((d) => (
                                        <article
                                            key={d.company}
                                            className="group cursor-grab rounded-xl border border-[#E5E7EB] bg-white p-3.5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#16A34A]/30 hover:shadow-md"
                                        >
                                            <p className="flex items-center gap-1.5 text-[12px] font-bold text-[#111827]">
                                                <Building2 className="h-3 w-3 text-[#94A3B8]" />
                                                {d.company}
                                            </p>
                                            <p className="mt-1.5 font-display text-[15px] font-extrabold text-[#16A34A]">{d.value}</p>
                                            <div className="mt-2.5 flex items-center justify-between">
                                                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#111827] text-[9px] font-bold text-white">{d.owner}</span>
                                                <span className="flex items-center gap-1 text-[10px] font-medium text-[#94A3B8]">
                                                    <CalendarDays className="h-3 w-3" /> {d.close}
                                                </span>
                                            </div>
                                            <div className="mt-2.5 flex items-center gap-1.5">
                                                <div className="h-1 flex-1 rounded-full bg-[#F1F5F9]">
                                                    <div className="h-1 rounded-full" style={{ width: `${d.prob}%`, background: col.color }} />
                                                </div>
                                                <span className="text-[9px] font-bold text-[#64748B]">{d.prob}%</span>
                                            </div>
                                        </article>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Reveal>
            <Reveal delay={0.1}>
                <div className="mt-10 text-center">
                    <a
                        href="#pricing"
                        data-testid="pipeline-explore-link"
                        className="group inline-flex items-center gap-2 rounded-full border border-[#16A34A]/30 bg-[#F0FDF4] px-6 py-3 text-sm font-semibold text-[#15803D] transition-all hover:-translate-y-0.5 hover:bg-[#16A34A] hover:text-white hover:shadow-lg hover:shadow-[#16A34A]/25"
                    >
                        Explore Sales Pipeline
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </a>
                </div>
            </Reveal>
        </Container>
    </section>
);

export default Pipeline;
